import { useState } from 'react';
import type { TableExportDoc } from '@goyone/shared';
import { exportTableXlsx, printTablePath } from '../lib/tableExport';
import { DocPreview } from './DocPreview';
import { Icon } from './Icon';
import { Menu, type MenuAction } from './Menu';
import { useToast } from './Toast';

type Build = () => TableExportDoc | Promise<TableExportDoc>;

export interface ExportScope {
  key: string;
  /** يُلحق باسم الصيغة: «إكسل — {label}». */
  label: string;
  build: Build;
  /** تلميحٌ يظهر عند المرور. */
  hint?: string;
}

/**
 * زرّ «تصدير» بقائمةٍ: إكسل وPDF.
 *
 * يأخذ `build` — دالّةً تبني المستند حين يُطلب لا قبله، فلا تُبنى آلافُ
 * الصفوف مع كلّ رسم — أو `scopes` حين يكون للتصدير نطاقان (القائمة الحالية
 * والثلاث معًا). وPDF يُعرض في معاينة الطباعة داخل النظام، ومنها «حفظ كـ PDF».
 */
export function ExportMenu({
  build,
  scopes,
  disabled,
  label = 'تصدير',
}: {
  build?: Build;
  scopes?: ExportScope[];
  disabled?: boolean;
  label?: string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ path: string; title: string } | null>(null);

  const list: ExportScope[] = scopes ?? (build ? [{ key: 'main', label: '', build }] : []);

  const run = async (scope: ExportScope, format: 'xlsx' | 'pdf') => {
    setBusy(true);
    try {
      const doc = await scope.build();
      if (format === 'xlsx') await exportTableXlsx(doc);
      else setPreview({ path: printTablePath(doc), title: doc.title });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'تعذّر التصدير');
    } finally {
      setBusy(false);
    }
  };

  const name = (format: string, scope: ExportScope) => (scope.label ? `${format} — ${scope.label}` : format);
  const actions: MenuAction[] = [
    ...list.map((s) => ({
      key: `xlsx-${s.key}`,
      label: name('إكسل', s),
      icon: 'download' as const,
      title: s.hint,
      disabled: busy || disabled,
      onSelect: () => void run(s, 'xlsx'),
    })),
    ...list.map((s, i) => ({
      key: `pdf-${s.key}`,
      label: name('PDF', s),
      icon: 'printer' as const,
      title: s.hint,
      separated: i === 0,
      disabled: busy || disabled,
      onSelect: () => void run(s, 'pdf'),
    })),
  ];

  return (
    <>
      <Menu label={label} className="btn" actions={actions}>
        <Icon name="download" size={15} />
        {busy ? 'جارٍ التصدير…' : label}
      </Menu>
      {preview && <DocPreview path={preview.path} title={preview.title} onClose={() => setPreview(null)} />}
    </>
  );
}
